/** Form Validation : Configure and Load PristineJS
 *
 * For backend validation and test, you can turn off front side validation by removing the shouldValidate class on any form
 *
 * Usage :
 *  - add a class `shouldValidate` to any `form`
 *  - add condition on html inputs
 *  - call FormValidator.init()
 *
 * Built in validators :
 * ---------------------
 * required -> required or data-pristine-required -> 	Validates required fields
 * email -> type="email" or data-pristine-type="email" ->	Validates email
 * number -> type="number" or data-pristine-type="number"
 * integer -> data-pristine-type="integer"
 * minlength -> minlength="10" or data-pristine-minlength="10"
 * maxlength -> maxlength="10" or data-pristine-maxlength="10"
 * min -> min="20" or data-pristine-min="20"
 * max -> max="100" or data-pristine-max="100"
 * pattern -> pattern="/[a-z]+$/i" or data-pristine-pattern="/[a-z]+$/i" ( \ must be escaped -> replace with \\ )
 * equals -> data-pristine-equals="#field-selector" -> Check that two fields are equal
 * ---------------------
 *
 * To add custom validator refer to the library doc on https://pristine.js.org/
 *
 * /!\ For un unknown reason we cannot directly import from the npm package with rollup, so a copy of the minified version has been copied in the vendor folder
 * //TODO: fix import with Rollup
 *
 * /!\ //FIXME: We had to remove `select` from the pristine targets due to a conflict with behaviour.js (pristine.js#68)
 */

import Pristine from './vendor/pristine';

const pristineOptions = {
  // class of the parent element where the error/success class is added
  classTo: 'ep_field',
  errorClass: 'has-error',
  successClass: 'has-success',
  // class of the parent element where error text element is appended
  errorTextParent: 'ep_form-errors-wrapper',
  // type of element to create for the error text
  errorTextTag: 'div',
  // class of the error text element
  errorTextClass: 'ep_form-errors',
};

class FormValidator {
  constructor(formElement) {
    this.form = formElement;

    // init pristine with "live" option turned off (we'll do that ourself onblur)
    this.pristine = new Pristine(this.form, pristineOptions, true);

    // find the submit button for the form
    this.submitBtn = this.form.querySelector('button[type="submit"]');

    // find all the inputs (used for validation as we type)
    this.inputs = this.form.querySelectorAll('input, select');

    // submit button is disabled by default
    this.submitBtn.classList.add('ep_button-disable');

    // attach eventListeners
    this.attachListeners();

    // run the validation once at load (autocomplete or reload might have pre-filled the form)
    FormValidator.activateSubmitAsWeType.bind(this)();
  }

  /**
   * Attach event handlers
   * Mind that `this` in event handlers reference the event context not class instance, so bind current `this` if needed in the event
   */
  attachListeners() {
    // prevent form submission if form doesn't validate
    this.form.addEventListener(
      'submit',
      FormValidator.validateOnSubmit.bind(this)
    );

    this.submitBtn.addEventListener(
      'click',
      FormValidator.validateOnSubmit.bind(this)
    );

    // validate while typing, enable submit button and display errors
    this.inputs.forEach((input) => {
      // using `keyup` as `keypress` doesn't catch backspace and del event (we want to check *after* the char is added / removed)
      // input.addEventListener(
      //   'keyup',
      //   FormValidator.activateSubmitAsWeType.bind(this)
      // );
      input.addEventListener(
        'input',
        FormValidator.activateSubmitAsWeType.bind(this)
      );
      input.addEventListener(
        'change',
        FormValidator.validateInputAndActivateSubmitAsWeType.bind(this)
      );
    });
  }

  /**
   * Prevent form submission if form doesn't validate
   * Static Event Handler
   * `this` is bound to the class instance, so we can access data from the class
   * @param {onSubmit} event_ - submit event
   */
  static validateOnSubmit(event_) {
    if (!this.pristine.validate()) event_.preventDefault();
  }

  /**
   * Validate form on input changes and (de)activate the submit button
   * Static Event handler
   * `this` is bound to the class instance, so we can access data from the class
   */
  static activateSubmitAsWeType() {
    // validate the whole form silently
    if (this.pristine.validate(true)) {
      this.submitBtn.classList.remove('ep_button-disable');
      if (this.submitBtn.disabled) this.submitBtn.removeAttribute('disabled')
    } else {
      this.submitBtn.classList.add('ep_button-disable');
      if (!this.submitBtn.disabled) this.submitBtn.setAttribute('disabled', 'true')
    }
  }

  static validateInputAndActivateSubmitAsWeType(event_) {
    // validate the unique input and show error
    this.pristine.validate(event_.originalTarget);

    // silently validate the rest of the form
    FormValidator.activateSubmitAsWeType.bind(this)();
  }

  /**
   * Static initilizer
   * Find all form on the page and create a validator for it
   */
  static init() {
    document
      .querySelectorAll('form.shouldValidate')
      .forEach((form) => new FormValidator(form));
    console.log('Form Validation init : ok');
  }
}

export default FormValidator;
